import ApiClient from "@/api/ApiClient";
import UseStoreHandler from "@/composables/handlers/UseStoreHandler";
import { translate } from "@/composables/translate";

class UserService {
  #prefixUrl = 'api/user/';

  constructor() {
    this.api = new ApiClient(this.#prefixUrl);
  }

  /**
   * Tries to register new User
   * @param {Object{name: string, email: string, password: string}} form 
   * @returns 
   */
  async register(form = {}) {
    return await this.api.executeRequest('', form, this.api.methods.POST);
  }

  /**
   * Tries to save User
   * @param {Object} userForm 
   * @returns {Response}
   */
  async saveUser(userData = {}, uuid = null) {
    let response = null;

    if (userData.birthDate) {
      let datePart = userData.birthDate.split('T')[0];
      datePart = datePart.replaceAll('/', '-');

      if (datePart.charAt(2) === '-') {
        const parts = datePart.split('-');
        userData.birthDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      } else {
        userData.birthDate = datePart;
      }
    }

    if (uuid != null) {
      response = await this.api.executeRequest(`/${uuid}`, userData, this.api.methods.PUT);
    } else {
      response = await this.api.executeRequest('/save', userData, this.api.methods.POST);
    }

    if (response.success()) {
      const userData = response.getContent().content != undefined ? response.getContent().content : response.getContent();

      UseStoreHandler.getAuthStore().login(userData);
    }

    return response;
  }

  /**
   * Tries to change password
   * @param {String} oldPassword
   * @param {String} newPassword 
   * @returns {Response}
   */
  async changePassword(oldPassword = '', newPassword = '') {
    return await this.api.executeRequest("changepass", {
      oldPassword, newPassword
    }, this.api.methods.POST);
  }

  /**
   * Tries to change email
   * Note: this will request an email confirmation and will send a real email
   * @param {String} newEmail
   * @returns {Response}
   */
  async changeEmail(newEmail = '') {
    return await this.api.executeRequest("changeemail", {
      email: newEmail
    }, this.api.methods.POST);
  }

  /**
   * Tries to change email
   * Note: this will request an email confirmation and will send a real email
   * @param {String} newEmail
   * @returns {Response}
   */
  async sendNewVerificationEmail(email = null, password = null) {
    const response = await this.api.executeRequest("sendnewverificationemail", {
      email: email, password: password
    }, this.api.methods.POST);

    return response;
  }

  /**
   * Sends email verification code
   * @param {String} verificatonCode
   * @returns {Response}
   */
  async sendEmailVerificationCode(verificatonCode = '') {
    let response = await this.api.executeRequest(`confirmemail/${verificatonCode}`, {}, this.api.methods.POST);

    return response;
  }

  /**
   * Sends email password reset code
   * @param {String} email
   * @returns {Response}
   */
  async requestPasswordReset(email = '') {
    let response = await this.api.executeRequest(`resetpassword`, {email: email}, this.api.methods.POST);

    return response;
  }

  /**
   * Sends email password with code
   * @param {String} email
   * @param {String} code
   * @returns {Response}
   */
  async verifyPasswordResetCode(email = '', code = '') {
    let response = await this.api.executeRequest(`verifypasswordcode/${code}`, {email: email}, this.api.methods.POST);

    return response;
  }

  /**
   * Sends email password with code
   * @param {String} email
   * @param {String} code
   * @returns {Response}
   */
  async updatePasswordWithCode(email = '', code = '', password = '') {
    let response = await this.api.executeRequest(`verifypasswordcode/${code}`, {email: email, newPassword: password}, this.api.methods.POST);

    return response;
  }

  /**
   * Checks password
   * @param {string} password 
   * @returns {object{success: boolean, message: string}}
   */
  checkPassword(password = '') {
    let response = {
      success: false,
      message: ''
    }
    if (password.length < 6) {
      response.message = translate('password is too shot, it must have more than 6 characters length', 'ucfirst');
      return response;
    }
    if (password.length > 20) {
      response.message = translate('password is too long, it must have more than 20 characters length', 'ucfirst');
      return response;
    }
    const specialCharRegex = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
    if (!specialCharRegex.test(password)) {
      response.message = translate("invalid password, enter a special character", 'ucfirst');
      return response;
    }
    const numberRegex = /[0-9]/;
    if (!numberRegex.test(password)) {
      response.message = translate("invalid password, enter a number", 'ucfirst');
      return response;
    }
    const upperCasaRegex = /[A-Z]/;
    if (!upperCasaRegex.test(password)) {
      response.message = translate("invalid password, enter an uppercase letter", 'ucfirst');
      return response;
    }
    const lowerCaseRegex = /[a-z]/;
    if (!lowerCaseRegex.test(password)) {
      response.message = translate("invalid password, enter a lowercase letter", 'ucfirst');
      return response;
    }
    response.success = true;
    return response;
  }

  /**
   * Chekcs email
   * @param {string} email 
   * @returns {boolean}
   */
  checkEmail(email = '') {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  }
}

export default new UserService();
